import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useMemo, useState } from 'react'
import { Vouchers } from '../../models/promotion-code-overview-response.model'
import { Box, Checkbox, IconButton, Typography, Stack } from '@mui/material'
import { SecureDataField } from 'src/Elements/Data'
import { currencyFormatter } from '../../utils'
import { J2Delete } from '@e3dc-react/icons'
import EditIcon from '@mui/icons-material/Edit'
import { isBefore, isAfter, format } from '@e3dc-react/shell/Libs/date'
import PromotionCodeWarning from './PromotionCodeWarning'

interface PromotionCodeTableProps {
  remainingBudget: number
  tableData: Vouchers[]
  onPromotionCodeDelete: (id: string) => void
  onPromotionCodeEdit: (id: string, amount: number, remainingBudget: number, editable: boolean, note?: string | null) => void
}

const PAGE_ROWS_LIMIT = 10

const PromotionCodesTable: React.FC<PromotionCodeTableProps> = ({ tableData, remainingBudget, onPromotionCodeDelete, onPromotionCodeEdit }) => {
  const { t } = useCustomTranslation()
  const [isRedeemedFilter, setIsRedeemedFilter] = useState(false)
  const [isExpiredFilter, setIsExpiredFilter] = useState(false)

  const filteredData = tableData.filter(voucher => {
    const isExpired = isBefore(new Date(voucher.valid_until), new Date())
    const isValid = isAfter(new Date(voucher.valid_until), new Date())

    if (isRedeemedFilter && isExpiredFilter) {
      return voucher.redeemed || isExpired
    }

    if (isRedeemedFilter) {
      return voucher.redeemed
    }

    if (isExpiredFilter) {
      return isExpired
    }

    return !voucher.redeemed && isValid
  })

  const renderEditableColumn = (editable: boolean, id: string, amount: number, note?: string | null): JSX.Element => (
    <Box>
      <IconButton size="large" onClick={() => onPromotionCodeEdit(id, amount, remainingBudget, editable, note)}>
        <EditIcon color={'primary'} />
      </IconButton>
      <IconButton size="large" onClick={() => onPromotionCodeDelete(id)}>
        <J2Delete color="primary" />
      </IconButton>
    </Box>
  )

  const renderCodeColumn = (code: string): JSX.Element => (
    <SecureDataField value={code} displayCopy clipboardMessage={t('shop.promotion-code.code-copied-label')} />
  )

  const renderValidUntilColumn = (validUntil: Date, redeemed?: boolean): JSX.Element => (
    <Box display="flex" alignItems="center">
      {format(validUntil, t('common.format.date-fns-date'))}
      {redeemed === false && <PromotionCodeWarning expiringDate={validUntil} />}
    </Box>
  )

  const header = useMemo(
    () => [
      {
        value: 'code',
        label: t('shop.promotion-code.table-code'),
        sortFunction: (a: string, b: string) => a.localeCompare(b),
        valueRender: (value: string) => {
          return renderCodeColumn(value)
        },
      },
      {
        value: 'created_at',
        label: t('shop.promotion-code.table-created-date'),
        sortFunction: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
        valueRender: (value: Date) => format(value, t('common.format.date-fns-date')),
      },
      {
        value: 'valid_until',
        label: t('shop.promotion-code.table-valid-until-date'),
        sortFunction: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
        valueRender: (value: Date, _index: number, item?: Vouchers) => {
          if (item) {
            return renderValidUntilColumn(value, item?.redeemed)
          }
        },
      },
      {
        value: 'value',
        label: t('shop.promotion-code.table-value'),
        sortFunction: (a: string, b: string) => Number(a) - Number(b),
        valueRender: (value: string) => <Typography variant="body2">{currencyFormatter(Number(value), 0)}</Typography>,
      },
      {
        value: 'redeemed',
        label: t('shop.promotion-code.table-redeemed'),
        sortFunction: (a: boolean, b: boolean) => (a === b ? 0 : a ? 1 : -1),
        valueRender: (value: boolean) => (
          <Typography variant="body2">
            {value ? t('shop.promotion-code.redeemed-true-text') : t('shop.promotion-code.redeemed-false-text')}
          </Typography>
        ),
      },
      {
        value: 'note',
        label: t('shop.promotion-code.table-note'),
        sortFunction: (a: string | null, b: string | null) => {
          if (a === null) return 1
          if (b === null) return -1
          return a.localeCompare(b)
        },
        valueRender: (value: string) => (
          <Typography variant="body2" sx={{ maxWidth: '170px' }}>
            {value}
          </Typography>
        ),
      },
      {
        value: 'editable',
        label: t('shop.promotion-code.table-status'),
        isSortable: false,
        valueRender: (value: boolean, _index: number, item?: Vouchers) => {
          if (item) {
            return renderEditableColumn(value, item.id, item.value, item.note)
          }
        },
      },
    ],
    //eslint-disable-next-line
    [t]
  )

  return (
    <Paper title={t('shop.promotion-code.table-header-label')}>
      <Stack direction="row" justifyContent="flex-start" spacing={2}>
        <Box display="flex" alignItems="center">
          <Checkbox checked={isRedeemedFilter} onChange={e => setIsRedeemedFilter(e.target.checked)} />
          <Typography variant="body2">{t('shop.promotion-code.extend-redeemed')}</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Checkbox checked={isExpiredFilter} onChange={e => setIsExpiredFilter(e.target.checked)} />
          <Typography variant="body2">{t('shop.promotion-code.extend-expired')}</Typography>
        </Box>
      </Stack>

      <TableComplete
        header={header}
        data={filteredData}
        coloredColumn
        resultLimit={PAGE_ROWS_LIMIT}
        initialSort={{
          name: 'created_at',
          direction: 'desc',
        }}
      />
    </Paper>
  )
}

export default PromotionCodesTable
